/**
 * S6 („Keine Sackgassen“): eine erwartbare Sperre an Ort und Stelle. Ein ruhiger Hinweis (kein roter „Etwas ist
 * schiefgelaufen“-Kasten) mit einem Satz „Geht gerade nicht: <Grund>“ und EINEM Knopf mit dem Ausweg.
 * Die Zuordnung Fehlercode -> Satz/Ausweg steht zentral in `lib/sperren.ts`; hier wird nur gezeigt.
 *
 * Einsatz: direkt an der Aktion, die gescheitert ist (Aktionskarte, Senden-Dialog) — der Aufrufer fängt den Fehler mit
 * `sperreAusFehler(err)`; ist das Ergebnis `null`, bleibt der Fehler laut wie bisher (`ErrorNote`).
 */
import { Button, ButtonRouterLink, Icon } from "./components";
import { AUSWEG_ICON, AUSWEG_LABEL, SPERRE_VORSPANN, type SperrLage } from "../lib/sperren";
import { abrechnungPfad, angebotPfad, angebotSendenPfad } from "../lib/pfade";

export function SperreVorOrt({
  sperre,
  eventId,
  onNeuLaden,
  onNeuSenden,
  onTerminAendern,
  onGueltigkeitVerlaengern,
  testId = "sperre-vor-ort",
}: {
  sperre: SperrLage;
  /** Für die Ausweg-Links (Senden, Angebot, Abrechnung). Fehlt er, fällt der Knopf auf „Neu laden“ zurück. */
  eventId?: string;
  /** „Neu laden“ / „Fassung aktualisieren“: Seite bzw. Abfragen frisch holen. */
  onNeuLaden: () => void;
  /** Liegt der Senden-Dialog auf derselben Seite (Builder), öffnet der Knopf ihn statt zu navigieren. */
  onNeuSenden?: () => void;
  /** „Termin ändern“ (Veranstaltung liegt in der Vergangenheit): öffnet die Eckdaten. Fehlt er, fällt der Knopf auf „Neu laden“ zurück. */
  onTerminAendern?: () => void;
  /** „Gültigkeit verlängern“: öffnet den Verlängern-Dialog der Seite statt zur Angebotsseite zu navigieren. */
  onGueltigkeitVerlaengern?: () => void;
  testId?: string;
}) {
  const label = AUSWEG_LABEL[sperre.ausweg];
  const icon = AUSWEG_ICON[sperre.ausweg];
  const klass = "!px-4 !py-2 !text-[13px] w-full justify-center sm:w-auto";
  const knopfId = `${testId}-knopf`;

  const link: string | null = !eventId
    ? null
    : sperre.ausweg === "neu_senden"
      ? onNeuSenden
        ? null
        : angebotSendenPfad(eventId)
      : sperre.ausweg === "nachtrag_erstellen" || (sperre.ausweg === "gueltigkeit_verlaengern" && !onGueltigkeitVerlaengern)
        ? angebotPfad(eventId)
        : sperre.ausweg === "zur_abrechnung"
          ? abrechnungPfad(eventId)
          : null;

  let knopf;
  if (link) {
    knopf = (
      <ButtonRouterLink to={link} variant="primary" className={klass} data-testid={knopfId}>
        <Icon name={icon} className="text-[16px]" /> {label}
      </ButtonRouterLink>
    );
  } else if (sperre.ausweg === "neu_senden" && onNeuSenden) {
    knopf = (
      <Button type="button" variant="primary" className={klass} onClick={onNeuSenden} data-testid={knopfId}>
        <Icon name={icon} className="text-[16px]" /> {label}
      </Button>
    );
  } else if (sperre.ausweg === "termin_aendern" && onTerminAendern) {
    knopf = (
      <Button type="button" variant="primary" className={klass} onClick={onTerminAendern} data-testid={knopfId}>
        <Icon name={icon} className="text-[16px]" /> {label}
      </Button>
    );
  } else if (sperre.ausweg === "gueltigkeit_verlaengern" && onGueltigkeitVerlaengern) {
    knopf = (
      <Button type="button" variant="primary" className={klass} onClick={onGueltigkeitVerlaengern} data-testid={knopfId}>
        <Icon name={icon} className="text-[16px]" /> {label}
      </Button>
    );
  } else {
    // „Neu laden“ / „Fassung aktualisieren“ — und der Rückfall, wenn ein Link-Ausweg keine Vorgangs-ID hat.
    const nurNeuLaden = sperre.ausweg === "neu_laden" || sperre.ausweg === "fassung_aktualisieren";
    knopf = (
      <Button type="button" variant="primary" className={klass} onClick={onNeuLaden} data-testid={knopfId}>
        <Icon name={nurNeuLaden ? icon : AUSWEG_ICON.neu_laden} className="text-[16px]" /> {nurNeuLaden ? label : AUSWEG_LABEL.neu_laden}
      </Button>
    );
  }

  return (
    <div
      role="status"
      data-testid={testId}
      data-sperre-code={sperre.code ?? ""}
      className="flex flex-col gap-3 rounded-xl border border-[#E7C98A] bg-[#FBF3E2] px-4 py-3 text-[#6B4E12] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#E9D3A4] sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
    >
      <Icon name="lock_clock" className="hidden shrink-0 text-[20px] sm:block" />
      <p className="min-w-0 flex-1 text-[13px]">
        <Icon name="lock_clock" className="mr-1 align-[-3px] text-[16px] sm:hidden" />
        <span className="font-bold">{SPERRE_VORSPANN}</span> {sperre.grund}
      </p>
      {knopf}
    </div>
  );
}
